<template>
    <VPanelRow>
        <VTextarea
            class="simple-textarea--h2"
            disabled
            maxlength="255"
            :model-value="`${numeric.name} (${t('quarter' + quarter)})`"
        ></VTextarea>
    </VPanelRow>

    <VPanelRow padding separate>
        <VInfoList class="numeric-panel__info-list">
            <VInfoItem center :label="t('numeric.expected-value')">
                <VInput class="input-field--primary" disabled :model-value="expectedValue" numeric></VInput>
            </VInfoItem>

            <VInfoItem center :label="t('numeric.actual-value')">
                <VInput class="input-field--primary" disabled :model-value="actualValue" numeric></VInput>
            </VInfoItem>

            <VInfoItem center :label="t('numeric.completion')">
                <VInput class="input-field--primary" disabled :model-value="quarterCompletion.value"></VInput>
            </VInfoItem>

            <VInfoItem center :label="t('numeric-counter.units')">
                <VInput
                    class="input-field--primary"
                    disabled
                    :model-value="
                        counter && counterDivisor && counter.units != counterDivisor.units
                            ? counter.units + ', ' + counterDivisor.units
                            : counter.units
                    "
                ></VInput>
            </VInfoItem>

            <small
                class="text text--h7 text--negative"
                v-if="
                    numeric.counter?.units &&
                    numeric.counterDivisor?.units &&
                    numeric.counter.units.toLowerCase() !== numeric.counterDivisor.units.toLowerCase()
                "
            >
                {{ t('numeric-counter.units-not-match-description') }}
            </small>

            <VInfoItem center :label="t('numeric.condition')">
                <VInput
                    class="input-field--primary"
                    disabled
                    :model-value="t('numeric.condition-' + numeric.condition)"
                    numeric
                ></VInput>
            </VInfoItem>
        </VInfoList>
    </VPanelRow>

    <VPanelRow :label="counter.name" label-separate padding separate>
        <VInput
            v-model="counterValue"
            class="input-field--primary inline-50-x inline-fluid"
            numeric
            :placeholder="t('no-value')"
        ></VInput>
    </VPanelRow>

    <VPanelRow :label="counterDivisor.name" label-separate padding separate v-if="counterDivisor">
        <VInput
            v-model="counterDivisorValue"
            class="input-field--primary inline-50-x inline-fluid"
            numeric
            :placeholder="t('no-value')"
        ></VInput>
    </VPanelRow>

    <VPanelRow down end gap padding separate-bottom>
        <VButton class="button--primary button--negative button--vcenter" @click="$emit('close')">
            {{ t('cancel') }}

            <TimesSvg></TimesSvg
        ></VButton>

        <VButton class="button--primary button--green button--vcenter" @click="submit">
            {{ t('add-value') }}

            <ArrowLeftSvg class="rotate-180"></ArrowLeftSvg
        ></VButton>
    </VPanelRow>
</template>
<script setup lang="ts">
// Svg
import TimesSvg from '../assets/times.svg';
import ArrowLeftSvg from '../assets/arrow-left.svg';

// Components
import VPanelRow from './VPanelRow.vue';
import VInfoList from './VInfoList.vue';
import VInfoItem from './VInfoItem.vue';
import VTextarea from './VTextarea.vue';
import VInput from './VInput.vue';
import VButton from './VButton.vue';

// Other
import INumericCounter from '@/core/Models/INumericCounter';
import { PropType, defineProps, defineEmits, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import INumeric from '@/core/Models/INumeric';
import { INumericQuarterCompletion } from '@/mixins/NumericApi';
import { DateTime } from 'luxon';

const { t } = useI18n();
const emit = defineEmits(['submit', 'close']);

const props = defineProps({
    quarter: { type: Number, required: true },
    quarterCompletion: { type: Object as PropType<INumericQuarterCompletion>, required: true },
    actualValue: { type: Number, default: 0 },
    expectedValue: { type: Number, default: 0 },
    numeric: { type: Object as PropType<INumeric>, required: true },
    counter: { type: Object as PropType<INumericCounter>, required: true },
    counterDivisor: { type: [Object, null] as PropType<INumericCounter | undefined | null>, required: true },
});

let counterValue = ref<number | null>(null);
let counterDivisorValue = ref<number | null>(null);

async function submit() {
    const result = [];

    let date = DateTime.utc();

    var maxDate = DateTime.utc()
        .set({ year: props.numeric.year, month: (props.quarter - 1) * 3 + 1 })
        .endOf('quarter');
    if (date > maxDate) {
        date = maxDate;
    }

    var minDate = DateTime.utc()
        .set({ year: props.numeric.year, month: (props.quarter - 1) * 3 + 1 })
        .startOf('quarter');
    if (date < minDate) {
        date = minDate;
    }

    if (counterValue.value !== null && counterValue.value !== undefined) {
        result.push({
            counterId: props.counter.id,
            value: counterValue.value,
            tagName: props.numeric.counterTagName,
            tagValue: props.numeric.counterTagValue,
            date,
        });
    }

    if (props.counterDivisor && counterDivisorValue.value) {
        result.push({
            counterId: props.counterDivisor.id,
            value: counterDivisorValue.value,
            tagName: props.numeric.counterDivisorTagName,
            tagValue: props.numeric.counterDivisorTagValue,
            date,
        });
    }

    if (result.length) {
        emit('submit', result);
    }
    emit('close');
}
</script>
<style lang="scss"></style>
